import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SearchInput from '../../shared/chat-bar/search-input';
import { useTheme } from '../../theme-context';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { MEMBER_STATUS } from '../../utils/constants/member-status';
import {
    tryToAddActionForChat,
    tryToFetchSingleChat,
} from '../../store/brokers/partners/history-chats/historyChatsSlice';
import { tryToDownloadMonthlyBank } from '../../store/brokers/partners/transactions/transactionsSlice';
import { tryToCreateChatMessage, tryToUploadChatFiles } from '../../store/brokers/partners/chats/ChatsSlice';
import TeamsModal from '../teams/TeamsModal';
import AiMessages from '../../shared/chat-messages/ai-messages';
import ClientMessages from '../../shared/chat-messages/client-messages';
import ReportIssuesModal from '../report-issue/ReportIssueModal';
import BankReconModal from '../bank-recon/BankReconModal';
import moment from 'moment';
import { exportBankReconExcel } from '../../utils/export-recon-excel';


interface ChatDrawerProps {
    isOpen: boolean;
    action?: any;
    historyId?: string;
    triggerId: string;
    chatData: any;
    onClose(): void;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({
    isOpen,
    triggerId,
    historyId,
    chatData,
    action,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const [chatId, setChatId] = useState<string>();
    const [isTyping, setIsTyping] = useState(false);
    const [allMessages, setAllMessages] = useState<any>();
    const [isWaiting, setIsWaiting] = useState<boolean>(false);
    const [showTeamsModal, setShowTeamsModal] = useState<boolean>(false);
    const [showBankModal, setShowBankModal] = useState<{ show: boolean, month?: any }>();
    const [uploadedFiles, setUploadedFiles] = useState<any>();
    const [uploadedAll, setUploadedAll] = useState<boolean>(false);
    const [reportModal, setReportModal] = useState<{ show: boolean, messageId: string }>();
    const scrollableRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const hasStartedChat = useRef(false);
    const autoConfirmRef = useRef<{ [key: string]: boolean }>({});
    const chatUi = useAppSelector((state) => state.chatUi);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, section: string) => {
        const files = e.target.files;
        if (!files) return;

        setUploadedFiles((prev: any) => ({
            ...prev,
            [section]: [...(prev?.[section] || []), ...Array.from(files)],
        }));
    };

    const removeFile = (section: string, index: number) => {
        setUploadedFiles((prev: any) => ({
            ...prev,
            [section]: prev[section].filter((_: any, i: number) => i !== index),
        }));
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return () => clearTimeout(timeout);
    }, [allMessages, isTyping, isWaiting]);

    const onGettingChatHistory = async () => {
        if (historyId && !(chatUi?.chatData && chatUi?.chatData?.length > 0)) {
            try {
                const response = await dispatch(tryToFetchSingleChat(historyId)).unwrap();
                const formatData = response?.data?.map((item: any) => {
                    if (item?.agent) return { agent: { ...item, ...item.agent } };
                    else return { user: { ...item, ...item.user } };
                });
                setAllMessages(formatData);
            } catch (err) {
                setAllMessages(chatId ? chatUi?.chatData || undefined : undefined);
            }
        }
    };

    const onGetChatResponse = async (userMessage: any, options?: { silent?: boolean }) => {
        // const updated = allMessages ? [...allMessages, userMessage] : [userMessage];

        if (!options?.silent) {
            setAllMessages((prev: any) => (prev ? [...prev, userMessage] : [userMessage]));

            setTimeout(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
        }

        setIsWaiting(true);

        const payload: any = {
            topic: chatId,
            message: userMessage?.user?.message,
            files: userMessage?.user?.files || [],
            stage: allMessages && allMessages?.length > 0 ? allMessages?.[allMessages?.length - 1]?.agent?.stage : undefined
        };

        try {
            const response = await dispatch(tryToCreateChatMessage(payload)).unwrap();
            const data = response?.data;
            const agentReply = {
                agent: {
                    ...data || {},
                    message: data?.message,
                    files: data?.files || data?.files || [],
                    createdAt: data?.createdAt,
                    options: data?.options,
                    _id: response?.data?.agentMessageId || data?.agent?._id,
                },
            };

            setAllMessages((prev: any) => {
                const base = prev || [];
                return [...base, agentReply];
            });
        } catch (error) {
            console.error('Failed to send chat message:', error);
        } finally {
            setIsWaiting(false);
        }
    };

    const onStartChat = async () => {
        if (historyId || chatId) return;
        if (action) {
            setAllMessages([
                {
                    user: {
                        message: action?.text,
                        files: [],
                        createdAt: new Date(),
                    },
                },
            ])
        }
        setIsWaiting(true);
        try {
            if (action) {
                const data = {
                    actionSelected: action?.enum,
                    message: action?.text,
                    files: [],
                };
                const response = await dispatch(tryToCreateChatMessage(data)).unwrap();
                setAllMessages([
                    {
                        user: {
                            message: action?.text,
                            files: [],
                            createdAt: new Date(),
                        },
                    },
                    {
                        agent: {
                            ...response?.data || {},
                            message: response?.data?.message,
                            files: response?.data?.files || [],
                            createdAt: new Date(),
                            _id: response?.data?.metadata?.messageId,
                        },
                    },
                ]);
                setChatId(response?.data?.topic);
            } else {
                const chatDataForm = { message: '' }
                const response = await dispatch(tryToCreateChatMessage(chatDataForm)).unwrap();
                setAllMessages([
                    {
                        agent: {
                            ...response?.data || {},
                            message: response?.data?.message,
                            files: response?.data?.files || [],
                            createdAt: new Date(),
                            _id: response?.data?.agentMessageId,
                        },
                    },
                ]);
                setChatId(response?.data.topic);
            }
        } catch (err) {
            console.error(err)
        } finally {
            setIsWaiting(false);
        }
    };

    useEffect(() => {
        if (historyId) {
            setChatId(historyId);
            onGettingChatHistory();
        }
    }, [historyId]);

    useEffect(() => {
        if (!historyId && !hasStartedChat.current) {
            hasStartedChat.current = true;
            onStartChat();
        }
    }, []);

    useEffect(() => {
        if (chatData && !action && !allMessages) {
            setAllMessages(chatUi?.chatData);
        }
        // if (!(chatData && chatData?.length > 0)) {
        //     onStartChat();
        // }
    }, [chatData, action, historyId]);

    useEffect(() => {
        if (!allMessages || allMessages.length === 0) return;

        const lastAgent = allMessages[allMessages.length - 1]?.agent;
        if (!lastAgent) return;

        const key = lastAgent?._id || `${lastAgent.stage}-${lastAgent.createdAt}`;
        if (autoConfirmRef.current[key]) return;

        if (
            lastAgent.stage === 'select_month' &&
            typeof lastAgent.message === 'string' &&
            lastAgent.message.includes("I'll generate the month-end report")
        ) {
            autoConfirmRef.current[key] = true;

            const silentUserMessage = {
                user: {
                    message: 'yes',
                    files: [],
                    createdAt: new Date(),
                },
            };

            onGetChatResponse(silentUserMessage, { silent: true });
        }
    }, [allMessages]);

    const onClickMessageAction = async (action: string, id: string) => {
        if (action === 'report') {
            setReportModal({
                show: true,
                messageId: id
            })
            return;
        }
        try {
            await dispatch(tryToAddActionForChat({ id, data: { action } })).unwrap();
            const formatAllMessages = allMessages?.map((item: any) => {
                if (item?.agent?._id === id) {
                    const key = action === 'like' ? 'liked' : action === 'dislike' ? 'disliked' : 'favorite';
                    return { agent: { ...item.agent, [key]: !item.agent[key] } };
                }
                return item;
            });
            setAllMessages(formatAllMessages);
        } catch (err) {
            console.error(err);
        }
    };

    const onGettingExcelData = async (data: any) => {
        try {
            const dataForm: any = { layer: 1, filterMonth: data };
            const response = await dispatch(tryToDownloadMonthlyBank(dataForm)).unwrap();
            exportBankReconExcel(response, data)
        } catch (err) {
            console.error(err)
        }
    }

    const onClickLinks = (type: string, data?: any) => {
        if (type === 'teams') setShowTeamsModal(true);
        if (type === 'bank') setShowBankModal({ show: true, month: data });
        if (type === 'excel-bank') {
            onGettingExcelData(data)
        }
    };

    const onCloseLinksModals = () => setShowTeamsModal(false);

    const onSuccessfullyDeleted = () => {
        onGetChatResponse({
            user: {
                message: "I'm done with offboarding thank you",
                files: [],
            },
        });
    };

    const onClickOneOption = (option: any) => {
        onGetChatResponse({
            user: {
                message: `${option?.key ? option?.key + '. ' : ''}${option?.value}`,
                files: [],
            },
        });
    }

    const onSuccessReport = () => {
        onGetChatResponse({
            user: {
                message: '*Reported an issue.',
                files: [],
            },
        });
    }

    const onCloseReportModal = () => {
        setReportModal(undefined)
    }

    useEffect(() => {
        if (allMessages && allMessages?.[allMessages?.length - 1]?.agent?.stage === 'upload_template_files') {
            const uploadStageMessage = allMessages?.find((msg: any) => msg?.agent && msg?.agent?.stage === 'upload_template_files');
            const allRequiredFilesUploaded = uploadStageMessage ? Object.keys(uploadStageMessage.agent.files).every(
                key => Array.isArray(uploadedFiles[key]) && uploadedFiles[key].length > 0
            )
                : false;
            setUploadedAll(allRequiredFilesUploaded)
        }
    }, [uploadedFiles])

    const onFinishUploading = async () => {
        setUploadedAll(false)

        const uploadedTemplateFiles: { name: string; link: string; type?: string }[] = [];

        if (uploadedFiles && Object.keys(uploadedFiles).length > 0) {
            for (const [type, files] of Object.entries(uploadedFiles)) {
                if (!Array.isArray(files) || files.length === 0) continue;

                const formData = new FormData();
                files.forEach(file => formData.append('files', file));
                formData.append('topicId', `${chatId}`);

                try {
                    const response = await dispatch(tryToUploadChatFiles(formData)).unwrap();
                    const typedFiles = response.map((file: { name: string; link: string }) => ({
                        ...file,
                        type
                    }));
                    uploadedTemplateFiles.push(...typedFiles);
                } catch (err) {
                    console.error(`Upload failed for type "${type}":`, err);
                }
            }
        }

        if (uploadedTemplateFiles?.length > 0) {
            const newMessage = {
                user: {
                    message: 'Finished',
                    sort: allMessages?.length + 2,
                    files: uploadedTemplateFiles,
                    createdAt: new Date(),
                    stage: 'upload_template_files'
                }
            };

            const updatedMessages = allMessages ? [...(allMessages || []), newMessage] : [newMessage];
            setAllMessages(updatedMessages);
            onGetChatResponse(newMessage);
            setUploadedFiles(undefined);
        }
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    layoutId={triggerId}
                    className='relative w-full h-screen bg-[#F5F5F5] dark:bg-bgColor-darkMode flex flex-col'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                    <header className='flex m-auto items-center justify-between p-4 min-w-[90%] max-w-[90%]'>
                        <div></div>
                        <div className='flex flex-row items-center cursor-pointer hover:opacity-80' onClick={onClose}>
                            <button className='mr-2 dark:text-primary-dark'>Close</button>
                            <img
                                src={theme === 'dark' ? '/assets/shared/close-icon-white.svg' : '/assets/shared/close-icon.svg'}
                                className='object-contain w-[25px]'
                            />
                        </div>
                    </header>

                    <div
                        ref={scrollableRef}
                        className='flex-1 overflow-y-auto px-4 py-2 max-w-[90%] min-w-[90%] mx-auto border-2 border-gray-200 bg-white dark:bg-[#3a3a3a] rounded-xl'
                    >
                        {allMessages?.map((item: any, idx: number) => {
                            const templateFiles = item?.agent?.stage === 'upload_template_files' ? allMessages && allMessages?.[idx + 1]?.user?.files || null : null
                            return (
                                <div key={idx}>
                                    {item?.agent ? (
                                        <div className='flex flex-row items-start'>
                                            {(idx === 0 || !allMessages[idx - 1]?.agent?.message) && (
                                                <div className='mt-4 mr-2'>
                                                    <img src='/assets/agents/charles-agent.svg' className='min-w-[35px] rounded-full' />
                                                </div>
                                            )}
                                            <AiMessages
                                                text={item.agent.message}
                                                files={item.agent.files}
                                                isNew={(allMessages?.length - 1) === idx}
                                                data={item.agent}
                                                setIsTyping={setIsTyping}
                                                isTyping={isTyping && ((allMessages?.length - 1) === idx)}
                                                showActions
                                                considerDarkMode
                                                uploadedAll={uploadedAll}
                                                showSubmitTemplate={(allMessages?.length - 1) === idx}
                                                uploadedFiles={uploadedFiles}
                                                templateFiles={templateFiles}
                                                handleFileChange={handleFileChange}
                                                removeFile={removeFile}
                                                onClickAction={onClickMessageAction}
                                                onClickLinks={onClickLinks}
                                                onClickOptions={onClickOneOption}
                                                onFinishUploading={onFinishUploading}
                                            />
                                        </div>
                                    ) : (
                                        <ClientMessages
                                            color='bg-[#E9EAEB] !text-black'
                                            text={item.user.message}
                                            time={item.user.createdAt}
                                            considerDarkMode
                                            stage={item?.user?.stage}
                                            files={item.user.files}
                                        />
                                    )}
                                </div>
                            )
                        }
                        )}

                        {isWaiting && (
                            <div className='flex items-start gap-2 mt-3'>
                                <img src='/assets/agents/charles-agent.svg' className='min-w-[35px] rounded-full' />
                                <div className='text-gray-400 py-2 px-3 rounded-xl max-w-[85%] text-sm animate-pulse'>
                                    Charles is preparing your answer...
                                </div>
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    <div className='sticky bottom-0 py-4 bg-[#F5F5F5] dark:bg-bgColor-darkMode'>
                        <SearchInput
                            placeholder='Ask Charles for something'
                            chatData={allMessages}
                            chatId={chatId}
                            setUploadedTemplateFiles={setUploadedFiles}
                            uploadedTemplateFiles={uploadedFiles}
                            considerDarkMode
                            stage={allMessages && allMessages?.length > 0 ? allMessages?.[allMessages?.length - 1]?.agent?.stage : undefined}
                            options={allMessages && allMessages?.length > 0 ? allMessages?.[allMessages?.length - 1]?.agent?.options : undefined}
                            onClickOptions={onClickOneOption}
                            onSendingData={onGetChatResponse}
                            isWaiting={isWaiting || isTyping || (!uploadedAll && allMessages?.[allMessages?.length - 1]?.agent?.stage === 'upload_template_files')}
                        />
                    </div>
                </motion.div>
            )}

            {showTeamsModal && (
                <TeamsModal
                    openModal={showTeamsModal}
                    status={MEMBER_STATUS.find((item: any) => item?.value === 'live')}
                    handleCloseModal={onCloseLinksModals}
                    SuccessfullyDeleted={onSuccessfullyDeleted}
                />
            )}
            {reportModal?.show &&
                <ReportIssuesModal
                    openModal={reportModal?.show}
                    messageId={reportModal?.messageId || ''}
                    chatId={chatId || ''}
                    onSuccessReport={onSuccessReport}
                    handleCloseModal={onCloseReportModal}
                />
            }
            {showBankModal?.show &&
                <BankReconModal
                    open={showBankModal?.show}
                    monthSelected={showBankModal?.month ? moment(showBankModal?.month, 'MM/YYYY').toDate() : undefined}
                    onClose={() => setShowBankModal(undefined)}
                />
            }
        </AnimatePresence>
    );
};

export default ChatDrawer;
